import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Cost, Deal, DealBase, DealPartyE, DealViewRaw, Segment } from '@tradecafe/types/core'
import { DeepPartial, DeepReadonly, isDealConfirmed } from '@tradecafe/types/utils'
import { combineLatest, from, of } from 'rxjs'
import { catchError, defaultIfEmpty, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { DealApiService } from 'src/api/deal'
import { FileApiService } from 'src/api/file'
import { TOP_LEVEL_FILTERS } from 'src/components/deals-list/deals-list.filters'
import { environment } from 'src/environments/environment'
import { BusActions } from 'src/constants/bus-events'
import { DealFormDto, SegmentFormDto } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.schema'
import { DealSubmitService } from 'src/services/actions/deal-submit.service'
import { BusService } from 'src/services/bus.service'
import { isBwiInventory } from 'src/services/data/accounts.service'
import { actualizeToZeroPatch, unactualizeToZeroPatch } from 'src/services/data/costs.service'
import { DealElasticSearchService } from 'src/services/data/deal-elastic.service'
import { DealViewSaver2Service } from 'src/services/data/deal-view-saver2.service'
import { DealsStoreService } from 'src/services/data/deals-store.service'
import { DealsService } from 'src/services/data/deals.service'
import { queueMap, waitNotEmpty } from 'src/services/data/utils'
import { buildCopiedSegmentVisibilityAttributes, freezeSegmentProviderAccounts } from 'src/services/data/visibility-control.utils'
import { isDealViewIncomplete } from 'src/shared/deal-view-incomplete/deal-view-incomplete.component'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { selectAccountEntities } from './accounts'
import { selectCarrierEntities } from './carriers'
import { prepareDealsPayload, prepareFiltersPayload } from './deal-view-filters.utils'
import { actualizeCostToZero, actualizeCostToZeroFailure, actualizeCostToZeroSuccess, approveDeringerPdf, approveDeringerPdfFailure, approveDeringerPdfSuccess, autosaveDealCost, autosaveDealCostFailure, autosaveDealCostSuccess, autosaveDealDetails, autosaveDealSegment, autosaveDealSegmentFailure, autosaveDealSegmentSuccess, cloneDeal, cloneDealFailure, cloneDealOperationCompleted, cloneDealSuccess, copyCloneDeal, copyCloneDealFailure, copyCloneDealSuccess, copySegments, copySegmentsFailure, copySegmentsSuccess, loadDealFilters, loadDealFiltersFailure, loadDealFiltersSuccess, loadDealViews, loadDealViewsFailure, loadDealViewsSuccess, lockDeal, lockDealFailure, lockDealSuccess, massDealForm, massDealFormFailure, massDealFormSuccess, patchDealForm, patchDealProduct, patchDealProductFailure, patchDealProductSuccess, patchDealProducts, patchDealProductsFailure, patchDealProductsSuccess, rejectDeringerPdf, rejectDeringerPdfFailure, rejectDeringerPdfSuccess, reverseDeal, reverseDealFailure, reverseDealSuccess, saveDealForm, saveDealFormFailure, saveDealFormSuccess, setBillOfLadingNo, setBillOfLadingNoFailure, setBillOfLadingNoSuccess, setBookingId, setBookingIdFailure, setBookingIdSuccess, setBuyerIncotermLocation, setBuyerIncotermLocationFailure, setBuyerIncotermLocationSuccess, setContainerId, setContainerIdFailure, setContainerIdSuccess, setDealConsignee, setDealConsigneeFailure, setDealConsigneeSuccess, setDealNotifyParty, setDealNotifyPartyFailure, setDealNotifyPartySuccess, setDealDate, setDealDateFailure, setDealDateRange, setDealDateRangeFailure, setDealDateRangeSuccess, setDealDateSuccess, setDealShipper, setDealShipperFailure, setDealShipperSuccess, setDealSlaughterDate, setDealSlaughterDateFailure, setDealSlaughterDateSuccess, setDealStatus, setDealStatusFailure, setDealStatusNotes, setDealStatusNotes2, setDealStatusNotes2Failure, setDealStatusNotes2Success, setDealStatusNotesFailure, setDealStatusNotesSuccess, setDealStatusSuccess, setDealTextField, setDealTextFieldFailure, setDealTextFieldSuccess, setDealsColor, setDealsColorFailure, setDealsColorSuccess, setDestLocation, setDestLocationFailure, setDestLocationSuccess, setItemTypeId, setItemTypeIdFailure, setItemTypeIdSuccess, setMexInvNo, setMexInvNoFailure, setMexInvNoSuccess, setOriginLocation, setOriginLocationFailure, setOriginLocationSuccess, setSegmentAddress, setSegmentAddressFailure, setSegmentAddressSuccess, setSegmentCarrier, setSegmentCarrierFailure, setSegmentCarrierSuccess, setSegmentDate, setSegmentDateFailure, setSegmentDateSuccess, setSupplierIncotermLocation, setSupplierIncotermLocationFailure, setSupplierIncotermLocationSuccess, setVessel, setVesselFailure, setVesselSuccess, startWorkingOn, startWorkingOnFailure, startWorkingOnSuccess, submitDealForm, submitDealFormFailure, submitDealFormSuccess, toggleDealFlag, toggleDealFlagFailure, toggleDealFlagSuccess, toggleInalfrescoFlag, toggleInalfrescoFlagFailure, toggleInalfrescoFlagSuccess, togglePortalAccessFailure, unactualizeCostToZero, unactualizeCostToZeroFailure, unactualizeCostToZeroSuccess, unwindDeal, unwindDealFailure, unwindDealSuccess, updatePortalAccess } from './deal-view.actions'

@Injectable()
export class DealViewEffects {
  private store = inject(Store)
  private actions$ = inject(Actions)
  private Deals = inject(DealsService)
  private busService = inject(BusService)
  private DealApi = inject(DealApiService)
  private elastic = inject(DealElasticSearchService)
  private deals = inject(DealViewSaver2Service)
  private FileApi = inject(FileApiService)
  private toaster = inject(ToasterService)
  private DealSubmit = inject(DealSubmitService)
  private readonly DealsStore = inject(DealsStoreService)


  loadDealViews$ = createEffect(() => this.actions$.pipe(
    ofType(loadDealViews),
    map(action => ({
      action,
      payload: prepareDealsPayload(action.filters, action.page, { tableKey: action.tableKey }),
    })),
    switchMap(({action, payload}) =>
      this.elastic.fetchDeals({...payload, calculate: action.calculate, populate_intransit_balance: action.populate_intransit_balance}, action.tableKey).pipe(
        map(r => loadDealViewsSuccess({
          tableKey: action.tableKey,
          total: r.total,
          totals: r.totals,
          dealViews: r.data,
          payload,
          resetTable: action.resetTable,
        })),
        catchAndLog(this.toaster, 'Unable to fetch deals', loadDealViewsFailure
      )))))

  loadDealFilters$ = createEffect(() => this.actions$.pipe(
    ofType(loadDealFilters),
    map(action => ({
      tableKey: action.tableKey,
      payload: prepareFiltersPayload(action.filters, TOP_LEVEL_FILTERS[action.tableKey], action.tableKey),
    })),
    // NOTE: temporarily(?) disabled cache
    // distinctUntilChanged(isEqual),
    switchMap(({tableKey, payload}) => this.elastic.fetchDealFilters(payload, tableKey).pipe(
      map(result => loadDealFiltersSuccess({ tableKey, result })),
      catchError(error => of(loadDealFiltersFailure({ error }))),
    ))))

  startWorkingOn$ = createEffect(() => this.actions$.pipe(
    ofType(startWorkingOn),
    switchMap(action =>
      this.deals.startWorkingOn(action.deals).pipe(
        map(result => startWorkingOnSuccess({ deals: result })),
        tap(() => this.toaster.success('Deal Status updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal status', startWorkingOnFailure
      )))))

  setDealsColor$ = createEffect(() => this.actions$.pipe(
    ofType(setDealsColor),
    switchMap(action =>
      this.deals.setDealsColor(action.deals, action.color).pipe(
        map(deals => setDealsColorSuccess({ deals })),
        tap(() => this.toaster.success('Deal Color updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal color', setDealsColorFailure
      )))))

  toggleDealFlag$ = createEffect(() => this.actions$.pipe(
    ofType(toggleDealFlag),
    switchMap(action =>
      this.deals.toggleDealFlag(action.deal, action.fieldName).pipe(
        map(deal => toggleDealFlagSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal flag updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal flag', toggleDealFlagFailure
      )))))

  updatePortalAccess$ = createEffect(() => this.actions$.pipe(
    ofType(updatePortalAccess),
    switchMap(action =>
      this.deals.updatePortalAccess(action.dv, action.previousDv).pipe(
        map(deal => saveDealFormSuccess({ deal })),
        catchAndLog(this.toaster, 'Unable to update deal portal access', togglePortalAccessFailure
      )),
    )))

  toggleInalfrescoFlag$ = createEffect(() => this.actions$.pipe(
    ofType(toggleInalfrescoFlag),
    tap(() => this.toaster.progress('Updating deal date range...')),
    switchMap(action =>
      this.deals.toggleInalfrescoFlag(action.deal).pipe(
        map(deal => toggleInalfrescoFlagSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal flag updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal flag', toggleInalfrescoFlagFailure
      )))))

  setDealDateRange$ = createEffect(() => this.actions$.pipe(
    ofType(setDealDateRange),
    tap(() => this.toaster.progress('Updating deal date range...')),
    switchMap(action =>
      this.storeDeal(action.deal, {
        updated: action.deal.updated,
        details: { ...action.details, deal_id: action.deal.deal_id },
      }).pipe(
        map(deal => setDealDateRangeSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal date range updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal date range', setDealDateRangeFailure
      )))))

  setDealSlaughterDate$ = createEffect(() => this.actions$.pipe(
    ofType(setDealSlaughterDate),
    tap(() => this.toaster.progress('Updating deal slaughter date...')),
    switchMap(action =>
      this.storeDeal(action.deal, {
        updated: action.deal.updated,
        details: { ...action.details, deal_id: action.deal.deal_id },
      }).pipe(
        map(deal => setDealSlaughterDateSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal slaughter date updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal slaughter date', setDealSlaughterDateFailure
      )))))

  setDealDate$ = createEffect(() => this.actions$.pipe(
    ofType(setDealDate),
    tap(action => {if (action.recalculate) this.toaster.progress('Updating deal date...')}),
    switchMap(action =>
      this.deals.setDealDate(action.deal, action.fieldName, action.date, action.recalculate).pipe(
        map(deal => setDealDateSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal date updated successfully')),
        catchAndLog(this.toaster, 'Unable to update segment date', setDealDateFailure
      )))))

  setSegmentDate$ = createEffect(() => this.actions$.pipe(
    ofType(setSegmentDate),
    tap(() => this.toaster.progress('Updating segment date...')),
    withLatestFrom(this.store.select(selectAccountEntities)),
    switchMap(([action, accounts]) =>
      this.DealsStore.setSegmentDate(action.deal, action.segment, action.patch, accounts).pipe(
        map(deal => setSegmentDateSuccess({ deal })),
        tap(() => this.toaster.success('Segment date updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update segment date', setSegmentDateFailure
      )))))
  
  setSegmentCarrier$ = createEffect(() => this.actions$.pipe(
      ofType(setSegmentCarrier),
      tap(() => this.toaster.progress('Updating segment carrier...')),
      switchMap((action) =>
        this.DealsStore.setSegmentCarrier(action.deal, action.segment, action.carrierId).pipe(
          switchMap(() =>
            this.Deals.getDealView(action.deal.deal_id).pipe(
              switchMap((dv) =>
                this.deals.updatePortalAccess(
                  dv,
                  buildPreviousDealViewForSegmentCarrierChange(dv, action.segment),
                ).pipe(
                  map(deal => setSegmentCarrierSuccess({ deal })),
                ),
              ),
            ),
          ),
          tap(() => this.toaster.success('Segment carrier updated successfully')),
          this.catchConflict(),
          catchAndLog(this.toaster, 'Unable to update segment carrier', setSegmentCarrierFailure
        )))))

  copySegments$ = createEffect(() => this.actions$.pipe(
    ofType(copySegments),
    tap(action => this.toaster.progress(`Copying segment from ${action.source.deal.deal_id} ...`)),
    switchMap(action =>
      this.DealsStore.copySegments(action.source, action.destination, action.deleteSegments, action.exactCopy).pipe(
        switchMap(deals => deals.length > 0
          ? combineLatest(deals.map(deal =>
            this.refreshPortalAccess(
              deal.deal_id,
              action.destination.find(dv => dv.deal.deal_id === deal.deal_id),
            ).pipe(
              switchMap(refreshedDeal => this.applyCopiedSegmentVisibilityState(
                refreshedDeal,
                action.source,
                action.destination.find(dv => dv.deal.deal_id === deal.deal_id),
              )),
              catchError(() => of(deal)),
            ),
          ))
          : of(deals)),
        map(deals => copySegmentsSuccess({ deals })),
        tap(() => this.toaster.success('Segments copied successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, `Unable to copy segments`, copySegmentsFailure
      )))))

  setBookingId$ = createEffect(() => this.actions$.pipe(
    ofType(setBookingId),
    switchMap(action =>
      this.DealsStore.setBookingId(action.deal, action.bookingId).pipe(
        map(deal => setBookingIdSuccess({ deal })),
        tap(() => this.toaster.success('Booking id updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update booking id', setBookingIdFailure
      )))))

  setBillOfLadingNo$ = createEffect(() => this.actions$.pipe(
    ofType(setBillOfLadingNo),
    switchMap(action =>
      this.DealsStore.setBillOfLadingNo(action.deal, action.billOfLadingNo).pipe(
        map(deal => setBillOfLadingNoSuccess({ deal })),
        tap(() => this.toaster.success('Bill of lading number updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update bill of lading number', setBillOfLadingNoFailure
      )))))

  setContainerId$ = createEffect(() => this.actions$.pipe(
    ofType(setContainerId),
    switchMap(action =>
      this.DealsStore.setContainerId(action.deal, action.containerId).pipe(
        map(deal => setContainerIdSuccess({ deal })),
        tap(() => this.toaster.success('Container number updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update container number', setContainerIdFailure
      )))))

  setMexInvNo$ = createEffect(() => this.actions$.pipe(
    ofType(setMexInvNo),
    switchMap(action =>
      this.DealsStore.setMexInvNo(action.deal, action.mexInvNo).pipe(
        map(deal => setMexInvNoSuccess({ deal })),
        tap(() => this.toaster.success('Container number updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update container number', setMexInvNoFailure
      )))))

  setDealTextField$ = createEffect(() => this.actions$.pipe(
    ofType(setDealTextField),
    switchMap(action =>
      this.deals.setDealTextField(action.deal, action.fieldName, action.text).pipe(
        map(deal => setDealTextFieldSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealTextFieldFailure
      )))))

  setDealConsignee$ = createEffect(() => this.actions$.pipe(
    ofType(setDealConsignee),
    switchMap(action =>
      this.deals.setDealConsignee(action.deal, action.address).pipe(
        map(deal => setDealConsigneeSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealConsigneeFailure
      )))))

  setDealShipper$ = createEffect(() => this.actions$.pipe(
    ofType(setDealShipper),
    switchMap(action =>
      this.deals.setDealShipper(action.deal, action.address).pipe(
        map(deal => setDealShipperSuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealShipperFailure
      )))))

  setDealNotifyParty$ = createEffect(() => this.actions$.pipe(
    ofType(setDealNotifyParty),
    switchMap(action =>
      this.deals.setDealNotifyParty(action.deal, action.address).pipe(
        map(deal => setDealNotifyPartySuccess({ deals: [deal] })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealNotifyPartyFailure
      )))))

  setVessel$ = createEffect(() => this.actions$.pipe(
    ofType(setVessel),
    switchMap(action =>
      this.DealsStore.setVessel(action.deal, action.vessel).pipe(
        map(deal => setVesselSuccess({ deal })),
        tap(() => this.toaster.success('Segment updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update segment', setVesselFailure
      )))))

  setSegmentAddress$ = createEffect(() => this.actions$.pipe(
    ofType(setSegmentAddress),
    switchMap(action =>
      this.DealsStore.setSegmentAddress(action.deal, action.segment, action.fieldName, action.value).pipe(
        map(deal => setSegmentAddressSuccess({ deal })),
        tap(() => this.toaster.success('Segment address updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update segment address', setSegmentAddressFailure
      )))))

  setItemTypeId$ = createEffect(() => this.actions$.pipe(
    ofType(setItemTypeId),
    switchMap(action =>
      this.DealsStore.setItemTypeId(action.deal, action.itemTypeId).pipe(
        map(deal => setItemTypeIdSuccess({ deal })),
        tap(() => this.toaster.success('Item Type updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update deal', setItemTypeIdFailure
      )))))

  setOriginLocation$ = createEffect(() => this.actions$.pipe(
    ofType(setOriginLocation),
    switchMap(action =>
      this.DealsStore.setOriginLocation(action.deal, action.locationId).pipe(
        map(deal => setOriginLocationSuccess({ deal })),
        tap(() => this.toaster.success('Origin Location updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update deal', setOriginLocationFailure
      )))))

  setDestLocation$ = createEffect(() => this.actions$.pipe(
    ofType(setDestLocation),
    switchMap(action =>
      this.DealsStore.setDestLocation(action.deal, action.locationId).pipe(
        map(deal => setDestLocationSuccess({ deal })),
        tap(() => this.toaster.success('Destination Location updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update deal', setDestLocationFailure
      )))))

  setBuyerIncotermLocation$ = createEffect(() => this.actions$.pipe(
    ofType(setBuyerIncotermLocation),
    switchMap(action =>
      this.DealsStore.setBuyerIncotermLocation(action.deal, action.locationId).pipe(
        map(deal => setBuyerIncotermLocationSuccess({ deal })),
        tap(() => this.toaster.success('Buyer Incoterm Location updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update deal', setBuyerIncotermLocationFailure
      )))))

  setSupplierIncotermLocation$ = createEffect(() => this.actions$.pipe(
    ofType(setSupplierIncotermLocation),
    switchMap(action =>
      this.DealsStore.setSupplierIncotermLocation(action.deal, action.locationId).pipe(
        map(deal => setSupplierIncotermLocationSuccess({ deal })),
        tap(() => this.toaster.success('Supplier Incoterm Location updated successfully')),
        this.catchConflict(),
        catchAndLog(this.toaster, 'Unable to update deal', setSupplierIncotermLocationFailure
      )))))

  setDealStatus$ = createEffect(() => this.actions$.pipe(
    ofType(setDealStatus),
    switchMap(action =>
      from(this.deals.setDealStatus(action.deal, action.status)).pipe(
        map(() => setDealStatusSuccess()),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealStatusFailure
      )))))

  setDealStatusNotes$ = createEffect(() => this.actions$.pipe(
    ofType(setDealStatusNotes),
    switchMap(action =>
      this.deals.setDealStatusNotes(action.deal, action.notes).pipe(
        map(deal => setDealStatusNotesSuccess({ deal })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealStatusNotesFailure
      )))))

  setDealStatusNotes2$ = createEffect(() => this.actions$.pipe(
    ofType(setDealStatusNotes2),
    switchMap(action =>
      this.deals.setDealStatusNotes2(action.deal, action.notes).pipe(
        map(deal => setDealStatusNotes2Success({ deal })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', setDealStatusNotes2Failure
      )))))

  lockDeal$ = createEffect(() => this.actions$.pipe(
    ofType(lockDeal),
    switchMap(action => this.checkConflict(action.deal, action)),
    switchMap(action =>
      this.deals.lockDeal(action.deal, action.lock).pipe(
        map(deal => lockDealSuccess({ deal })),
        tap(() => this.toaster.success(`Deal ${action.lock ? 'lock' : 'unlock'}ed successfully`)),
        catchAndLog(this.toaster, `Unable to ${action.lock ? 'lock' : 'unlock'} deal`, lockDealFailure
      )))))

  copyCloneDeal$ = createEffect(() => this.actions$.pipe(
    ofType(copyCloneDeal),
    switchMap(action =>
      this.deals.copyClone(action.dealId, action.count).pipe(
        map(deals => copyCloneDealSuccess({ deals })),
        tap(() => this.toaster.info(`Your clones are being generated and indexed.  They should appear in the deals list shortly. Please refresh the page in a minute.`)),
        catchAndLog(this.toaster, `Unable to clone deal`, copyCloneDealFailure
      )))))

  cloneDeal$ = createEffect(() => this.actions$.pipe(
    ofType(cloneDeal),
    switchMap(action =>
      this.busService.startOperation(BusActions.CLONE_DEAL, operationId =>
        this.Deals.cloneDeals(action.dealId, action.payload, operationId).pipe(
          map(deals => cloneDealSuccess({ deals })),
          tap(() => this.toaster.success(`Deal cloned successfully`)),
          catchAndLog(this.toaster, `Unable to clone deal`, cloneDealFailure
        ))
      ).pipe(map(reaction => {
        if (reaction.type === 'scheduled') return reaction.data
        return cloneDealOperationCompleted({ dealIds: reaction.data.dealIds })
      }))
    )))

  approveDeringerPdf$ = createEffect(() => this.actions$.pipe(
    ofType(approveDeringerPdf),
    switchMap(action =>
      from(this.FileApi.approve(action.file_id)).pipe(
        switchMap(() => this.elastic.fetchDeal(action.deal_id, ['deringer_te_sent', 'deringer_data_received', 'deringer_pdf_received', 'deringer_pdf_bonds', 'deringer_approved_at', 'deringer_approved_by'])),
        tap(() => this.toaster.success('Deringer PDF aproved successfully')),
        map(deal => approveDeringerPdfSuccess({ deal })),
        catchAndLog(this.toaster, 'Unable to update deringer PDF', approveDeringerPdfFailure
      )))))

  rejectDeringerPdf$ = createEffect(() => this.actions$.pipe(
    ofType(rejectDeringerPdf),
    switchMap(action =>
      from(this.FileApi.reject(action.file_id)).pipe(
        switchMap(() => this.elastic.fetchDeal(action.deal_id, ['deringer_te_sent', 'deringer_data_received', 'deringer_pdf_received', 'deringer_pdf_bonds', 'deringer_approved_at', 'deringer_approved_by'])),
        tap(() => this.toaster.success('Deringer PDF rejected successfully')),
        map(deal => rejectDeringerPdfSuccess({ deal })),
        catchAndLog(this.toaster, 'Unable to reject deringer PDF', rejectDeringerPdfFailure
      )))))

  saveDealForm$ = createEffect(() => this.actions$.pipe(
    ofType(saveDealForm),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, action.dealForm).pipe(
        tap(deal => this.toaster.success(`Deal # ${deal.deal_id} saved successfully`)),
        map(deal => saveDealFormSuccess({ deal })),
        catchAndLog(this.toaster, `Unable to save Deal`, saveDealFormFailure
      ))
    })))

  patchDealForm$ = createEffect(() => this.actions$.pipe(
    ofType(patchDealForm),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, action.dealForm).pipe(
        tap(deal => this.toaster.success(`Deal # ${deal.deal_id} saved successfully`)),
        map(deal => saveDealFormSuccess({ deal })),
        catchAndLog(this.toaster, `Unable to save Deal`, saveDealFormFailure
      ))
    })))

  autosaveDealDetails$ = createEffect(() => this.actions$.pipe(
    ofType(autosaveDealDetails),
    switchMap(action =>
      this.storeDeal(action.deal, {
        updated: action.deal.updated,
        details: { ...action.patch, deal_id: action.deal.deal_id },
      }).pipe(
        tap(deal => this.toaster.success(`Deal # ${deal.deal_id} autosaved successfully`)),
        map(deal => saveDealFormSuccess({ deal })),
        catchAndLog(this.toaster, `Unable to autosave Deal`, saveDealFormFailure
      )))))

  submitDealForm$ = createEffect(() => this.actions$.pipe(
    ofType(submitDealForm),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, action.dealForm).pipe(
        switchMap(deal => this.refreshPortalAccess(deal.deal_id, action.dv)),
        switchMap(deal => from(
          action.skipConfirmationDialog
            ? this.DealSubmit.submitDeal(deal, { skipConfirmationDialog: true })
            : this.DealSubmit.submitDeal(deal),
        ).pipe(
          filter((submitted): submitted is Deal => submitted != null),
          tap(submitted => this.toaster.success(`Deal # ${submitted.deal_id} submitted successfully`)),
          map(submitted => submitDealFormSuccess({ deal: submitted })),
          defaultIfEmpty<
            ReturnType<typeof submitDealFormSuccess>,
            ReturnType<typeof submitDealFormFailure>
          >(submitDealFormFailure({ error: new Error('Cancelled') })),
        )),
        catchAndLog(this.toaster, `Unable to submit Deal`, submitDealFormFailure
      ))
    })))

  patchDealProduct$ = createEffect(() => this.actions$.pipe(
    ofType(patchDealProduct),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dealForm.updated,
        details: { deal_id: action.dealForm.details.deal_id },
        products: action.dealForm.products.map(({ offer_id, bid_id }, index) =>
          action.index === index
            ? { ...action.patch, offer_id, bid_id }
            : { offer_id, bid_id }),
      }).pipe(
        map(deal => patchDealProductSuccess({ deal })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', patchDealProductFailure
      ))
    })))

  patchDealProducts$ = createEffect(() => this.actions$.pipe(
    ofType(patchDealProducts),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dealForm.updated,
        details: { deal_id: action.dealForm.details.deal_id },
        products: action.dealForm.products.map(({ offer_id, bid_id }, index) =>
          ({ ...action.patches[index], offer_id, bid_id })),
      }).pipe(
        map(deal => patchDealProductsSuccess({ deal })),
        tap(() => this.toaster.success('Deal updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', patchDealProductsFailure
      ))
    })))

  autosaveDealCost$ = createEffect(() => this.actions$.pipe(
    ofType(autosaveDealCost),
    queueMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dv.deal.updated,
        details: { deal_id: action.dv.deal.deal_id },
        costs: action.costs.map((cost, index) =>
          action.index?.includes(index) ? cost : { cost_id: cost.cost_id }),
      }).pipe(
        switchMap(deal => shouldRefreshVisibilityForCostAutosave(action.dv.costs, action.costs, action.index)
          ? this.refreshPortalAccess(deal.deal_id, action.dv).pipe(
            map(refreshedDeal => ({ deal: refreshedDeal, visibilityRefreshFailed: false })),
            catchError(error => {
              console.error(`Unable to update visibility after saving costs for deal ${deal.deal_id}`, error)
              return of({ deal, visibilityRefreshFailed: true })
            }),
          )
          : of({ deal, visibilityRefreshFailed: false })),
        tap(({ visibilityRefreshFailed }) => visibilityRefreshFailed
          ? this.toaster.warning('Cost saved, but visibility could not be updated.')
          : this.toaster.success('Deal costs updated successfully')),
        map(({ deal }) => autosaveDealCostSuccess({ deal })),
        catchAndLog(this.toaster, 'Unable to update deal', autosaveDealCostFailure
      ))
    })))

  actualizeCostToZero$ = createEffect(() => this.actions$.pipe(
    ofType(actualizeCostToZero),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dv.deal.updated,
        details: { deal_id: action.dv.deal.deal_id },
        costs: action.dv.costs.map(cost =>
          action.cost_id === cost.cost_id
            ? { cost_id: cost.cost_id, ...actualizeToZeroPatch(cost) }
            : { cost_id: cost.cost_id }),
      }).pipe(
        map(deal => actualizeCostToZeroSuccess({ deal })),
        tap(() => this.toaster.success('Cost Actualized to $0 successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', actualizeCostToZeroFailure
      ))
    })))

  unactualizeCostToZero$ = createEffect(() => this.actions$.pipe(
    ofType(unactualizeCostToZero),
    switchMap(action => {
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dv.deal.updated,
        details: { deal_id: action.dv.deal.deal_id },
        costs: action.dv.costs.map(cost =>
          action.cost_id === cost.cost_id
            ? { cost_id: cost.cost_id, ...unactualizeToZeroPatch(cost) }
            : { cost_id: cost.cost_id }),
      }).pipe(
        map(deal => unactualizeCostToZeroSuccess({ deal })),
        tap(() => this.toaster.success('Cost Unctualized successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', unactualizeCostToZeroFailure
      ))
    })))

  autosaveDealSegment$ = createEffect(() => this.actions$.pipe(
    ofType(autosaveDealSegment),
    switchMap(action =>{
      const incomplete = isDealViewIncomplete(action.dv)
      if (incomplete) {
        this.toaster.warning(incomplete)
        return of(saveDealFormFailure({ error: new Error(incomplete) }))
      }
      return this.storeDeal(action.dv.deal, {
        updated: action.dealForm.updated,
        details: {
          deal_id: action.dealForm.details.deal_id,
          shipmentBolDate: action.dealForm.details.shipmentBolDate,
          buyerTermDate: action.dealForm.details.buyerTermDate,
          collectionDate: action.dealForm.details.collectionDate,
          supplierTermDate: action.dealForm.details.supplierTermDate,
          ...(action.dealForm.details.supplierBalanceLiabilityDate != null && {
            supplierBalanceLiabilityDate: action.dealForm.details.supplierBalanceLiabilityDate,
          }),
          antLiabilityDate: action.dealForm.details.antLiabilityDate,
          antLiabilityDateIsWeighted: action.dealForm.details.antLiabilityDateIsWeighted,
        },
        segments: action.dealForm.segments.map((segment, index) =>
          action.index === index
            ? { segment_id: segment.segment_id, ...action.patch }
            : { segment_id: segment.segment_id }),
      }).pipe(
        switchMap(deal => shouldRefreshVisibilityForSegmentAutosave(action.patch)
          ? this.refreshPortalAccess(deal.deal_id, action.dv).pipe(
            map(refreshedDeal => ({ deal: refreshedDeal, visibilityRefreshFailed: false })),
            catchError(error => {
              console.error(`Unable to update visibility after saving segments for deal ${deal.deal_id}`, error)
              return of({ deal, visibilityRefreshFailed: true })
            }),
          )
          : of({ deal, visibilityRefreshFailed: false })),
        tap(({ visibilityRefreshFailed }) => visibilityRefreshFailed
          ? this.toaster.warning('Segments saved, but visibility could not be updated.')
          : this.toaster.success('Deal segments updated successfully')),
        map(({ deal }) => autosaveDealSegmentSuccess({ deal })),
        catchAndLog(this.toaster, 'Unable to update deal', autosaveDealSegmentFailure
      ))
    })))

  massDealForm$ = createEffect(() => this.actions$.pipe(
    ofType(massDealForm),
    switchMap(action =>
      combineLatest(action.changes.map(({dv, patch}) =>
        this.storeDeal(dv.deal, { ...patch, updated: dv.deal.updated }).pipe(switchMap(stored =>
          // lock deal if was confirmed and if changed party was bwi inventory
          isDealConfirmed(dv.deal) && action.party && isBwiInventory(action.party === DealPartyE.buyer ? dv.deal.buyer_id : dv.deal.supplier_id)
            ? this.DealApi.lock(dv.deal.deal_id).pipe(map(r => r.data))
            : of(stored)))))),
        map(deals => massDealFormSuccess({ deals })),
        tap(() => this.toaster.success('Deals updated successfully')),
        catchAndLog(this.toaster, 'Unable to update deal', massDealFormFailure
      )))

  reverseDeal$ = createEffect(() => this.actions$.pipe(
    ofType(reverseDeal),
    tap(() => this.toaster.progress('Reversing deal...')),
    switchMap(action =>
      this.DealApi.unwind(action.deal_id).pipe(
        map(() => reverseDealSuccess({ deal_id: action.deal_id })),
        tap(() => this.toaster.success('Deal reversed successfully')),
        catchAndLog(this.toaster, 'Unable to reversed deal', reverseDealFailure
      )))))

  unwindDeal$ = createEffect(() => this.actions$.pipe(
    ofType(unwindDeal),
    tap(() => this.toaster.progress('Unwinding deal...')),
    switchMap(action =>
      this.DealApi.unwind(action.deal_id).pipe(
        map(() => unwindDealSuccess({ deal_id: action.deal_id })),
        tap(() => this.toaster.success('Deal unwinded successfully')),
        catchAndLog(this.toaster, 'Unable to unwind deal', unwindDealFailure
      )))))

  private checkConflict<T>(deal: DeepReadonly<DealBase>, action: T) {
    return from(this.Deals.hasDataConflicts(deal)).pipe(
      tap(hasDataConflicts => hasDataConflicts && this.toaster.error('Data conflict detected. Please reload page.')),
      filter(hasDataConflicts => !hasDataConflicts),
      map(() => action))
  }

  private storeDeal(deal: DeepReadonly<DealBase>, dealForm: DeepReadonly<DeepPartial<DealFormDto>>) {
    return this.DealsStore.storeDeal(deal, dealForm).pipe(this.catchConflict())
  }

  private refreshPortalAccess(dealId: string, previousDv?: DeepReadonly<DealViewRaw | Partial<DealViewRaw>>) {
    return this.store.select(selectCarrierEntities).pipe(
      waitNotEmpty(),
      take(1),
      switchMap(carriers =>
        this.Deals.getDealView(dealId).pipe(
          switchMap(dv =>
            this.deals.updatePortalAccess(
              dv,
              previousDv
                ? freezeSegmentProviderAccounts(previousDv, carriers) as DeepReadonly<DealViewRaw>
                : undefined,
            ),
          ),
        ),
      ),
    )
  }

  private applyCopiedSegmentVisibilityState(
    deal: Deal,
    source: DeepReadonly<Pick<DealViewRaw, 'deal' | 'segments'>>,
    previousDestination?: DeepReadonly<Pick<DealViewRaw, 'deal' | 'segments'>>,
  ) {
    return this.store.select(selectCarrierEntities).pipe(
      waitNotEmpty(),
      take(1),
      switchMap(carriers => {
        const attributes = buildCopiedSegmentVisibilityAttributes(deal, source, previousDestination, carriers)
        return attributes
          ? from(this.Deals.updateDeal({ deal_id: deal.deal_id, attributes }, { skip_calculations: 1 }))
          : of(deal)
      }),
    )
  }

  private catchConflict<T>() {
    return catchError<T, never>(error => {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        throw this.toaster.newError('Data conflict detected. Please reload page.')
      }
      throw error
    })
  }
}

export function buildPreviousDealViewForSegmentCarrierChange(
  currentDv: DeepReadonly<DealViewRaw>,
  previousSegment: DeepReadonly<Segment>,
): DeepReadonly<DealViewRaw> {
  return {
    ...currentDv,
    segments: currentDv.segments.map(segment =>
      segment.segment_id === previousSegment.segment_id ? previousSegment : segment,
    ),
  }
}

export function shouldRefreshVisibilityForSegmentAutosave(patch?: DeepReadonly<Partial<SegmentFormDto>>): boolean {
  if (!patch) return true

  const visibilityFields = new Set<keyof SegmentFormDto>([
    'carrierAccountId',
    'carrierId',
    'freightForwarderId',
    'type',
  ])
  return Object.keys(patch).some(field => visibilityFields.has(field as keyof SegmentFormDto))
}

export function shouldRefreshVisibilityForCostAutosave(
  previousCosts: DeepReadonly<Partial<Cost>[] | undefined>,
  nextCosts: DeepReadonly<Partial<Cost>[]>,
  indexes?: readonly number[],
): boolean {
  if (!indexes?.length) return true

  return indexes.some(index =>
    costVisibilitySnapshot(previousCosts?.[index]) !== costVisibilitySnapshot(nextCosts[index]))
}

function costVisibilitySnapshot(cost?: DeepReadonly<Partial<Cost>>): string {
  return JSON.stringify({
    provider: cost?.provider ?? null,
    service: cost?.service ?? null,
    type: cost?.type ?? null,
    productId: cost?.product_id ?? null,
    freightType: cost?.attributes?.freight_type ?? null,
  })
}
